/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.js';
import { CurrentData } from '../types';

interface ResetPasswordProps {
  onDataSend: (data: CurrentData, token: string) => void;
}

function ResetPassword({ onDataSend }: ResetPasswordProps) {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenData, setTokenData] = useState<{ email: string; token: string; expiresAt: number } | null>(null);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const token = searchParams.get('token');
    console.log('Token from URL:', token);
    
    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    try {
      // Decode base64 in browser
      const decoded = JSON.parse(atob(token));
      console.log('Decoded token data:', decoded);
      
      // Check if token is expired
      if (Date.now() > decoded.expiresAt) {
        setError('This reset link has expired. Please request a new one.');
        return;
      }

      setTokenData(decoded);
    } catch (err) {
      console.error('Error decoding token:', err);
      setError('Invalid reset token');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Update password in Firebase
      const response = await fetch(`${API_BASE_URL}/.netlify/functions/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: tokenData!.email,
          token: tokenData!.token,
          newPassword: password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      // Automatically sign in the user
      const userCredential = await signInWithEmailAndPassword(auth, tokenData!.email, password);
      const user = userCredential.user;
      
      // Get fresh ID token with custom claims
      const idTokenResult = await user.getIdTokenResult();
      const idToken = idTokenResult.token;
      
      // IMPORTANT: Get isAdmin from custom claims (more secure)
      const isAdmin = idTokenResult.claims.isAdmin || false;

      // Get user data from Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      // Create user data object
      const currentData: CurrentData = {
        success: true,
        data: {
          userId: user.uid,
          email: user.email || '',
          firstname: userData?.firstname || '',
          lastname: userData?.lastname || '',
          isAdmin: isAdmin, // ✅ Use custom claim, not Firestore
          tags: userData?.collectionHandles || [],
          salesDataByTag: [],
          tagsArray: userData?.collectionHandles || []
        }
      };

      console.log('User logged in after password reset');
      console.log('Admin status from custom claim:', isAdmin);
      
      // Pass data to parent component
      onDataSend(currentData, idToken);
      
      // Redirect to account page
      navigate('/account');
    } catch (err) {
      console.error('Error resetting password:', err);
      setError((err as Error).message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (error && !tokenData) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Reset Password</h2>
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
          {error}
        </div>
        <button 
          className="btn"
          onClick={() => navigate('/')}
        >
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Reset Your Password</h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="password">New Password:</label>
          <input
            id="password"
            type="password"
            placeholder="Enter new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            minLength={6}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="confirm-password">Confirm Password:</label>
          <input
            id="confirm-password"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            minLength={6}
          />
        </div>

        <button type="submit" className="btn" disabled={loading}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
}

export default ResetPassword;